/**
 * 10.9DR — REI DO VAZIO (boss HUMANOIDE / Comandante). Pedido do Thiago: um boss humano que
 * INVOCA tropas e bate pesado, em 2 fases. Modelo procedural (corpo, cabeça+coroa, capa, cetro),
 * mesma interface Boss dos demais (Apex Fang / Rift Seer / Colosso), então usa todo o wiring do
 * Game (HUD, selo da Fenda, coleira, recompensa, música). Fase 2 (<50% HP): fúria — mais rápido,
 * mais tropas e uma ONDA DE CHOQUE em anel.
 */
import * as THREE from "three";
import { applySkin } from "../enemies/CreatureSkins";
import { applyRim } from "../fx/RimLight";
import { GameConfig } from "../config/GameConfig";
import type { Damageable } from "../combat/Damageable";
import type { HookReaction } from "../player/HookSystem";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { events } from "../core/Events";
import type { Boss, BossDebug } from "./Boss";

const cfg = GameConfig.boss.voidKing;

export interface VoidKingDeps {
  player: Damageable;
  telegraph: TelegraphSystem;
  particles: ParticleSystem;
  summon: (count: number, center: THREE.Vector3) => void;
  impulse: (strength: number) => void;
}
export interface VoidKingOptions {
  event?: boolean;
  hpMult?: number;
  dmgMult?: number;
  spawnAt?: { x: number; z: number };
  tint?: number;
  titleKey?: string;
  arenaRadius?: number;
}

type KState = "intro" | "chase" | "attack" | "dead";
type KAttack = "slam" | "summon" | "shockwave";

const _v = new THREE.Vector3();
const _c = new THREE.Vector3();

export class VoidKing implements Boss {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly radius = cfg.radius;
  pulled = false;
  removeRequested = false;

  private state: KState = "intro";
  private stateTimer = cfg.introDuration;
  private attackName: KAttack | null = null;
  private atkPhase = 0;
  private atkTimer = 0;
  private phase = 1;
  private restTimer = 0;
  private reactStagger = 0;
  private facing = Math.PI;
  private flash = 0;
  private deathTimer = 0;
  private gait = 0;
  private readonly cd = { slam: 0, summon: 0, shockwave: 0 };
  private readonly pendingSlam: { x: number; z: number; r: number; t: number; dmg: number; kb: number }[] = [];
  private readonly pendingWave: { x: number; z: number; r: number; t: number; dmg: number; kb: number }[] = [];

  private readonly arenaCx: number;
  private readonly arenaCz: number;
  private readonly arenaR: number;
  private readonly dmgMult: number;
  private readonly isEvent: boolean;
  private readonly titleKey?: string;

  private readonly bodyMat: THREE.MeshStandardMaterial;
  private readonly capeMat: THREE.MeshStandardMaterial;
  private readonly glowMat: THREE.MeshStandardMaterial;
  private readonly rimMats: THREE.MeshStandardMaterial[] = [];
  private readonly legs: THREE.Object3D[] = [];
  private weaponArm!: THREE.Group;
  private cape!: THREE.Mesh;
  private auraPulse = 0;

  constructor(scene: THREE.Scene, private readonly deps: VoidKingDeps, opts?: VoidKingOptions) {
    this.isEvent = !!opts?.event;
    this.titleKey = opts?.titleKey;
    this.dmgMult = opts?.dmgMult ?? 1;
    this.arenaCx = opts?.spawnAt?.x ?? 0;
    this.arenaCz = opts?.spawnAt?.z ?? 0;
    this.arenaR = opts?.arenaRadius ?? cfg.arena.radius;
    this.health = new HealthComponent(Math.round(cfg.maxHp * (opts?.hpMult ?? 1)), undefined, () => this.die());

    const tint = opts?.tint ?? 0x9b6bff;
    this.bodyMat = new THREE.MeshStandardMaterial({ color: 0x241a3a, emissive: tint, emissiveIntensity: 0.5, roughness: 0.5, metalness: 0.3, flatShading: true });
    applySkin(this.bodyMat, "crystal", 0.03, 2);
    applyRim(this.bodyMat, { color: tint, power: 2.2, strength: 1.4 });
    this.rimMats.push(this.bodyMat);
    this.capeMat = new THREE.MeshStandardMaterial({ color: 0x160f2c, emissive: tint, emissiveIntensity: 0.22, roughness: 0.8, side: THREE.DoubleSide, flatShading: true });
    this.glowMat = new THREE.MeshStandardMaterial({ color: tint, emissive: tint, emissiveIntensity: 1.4, roughness: 0.3 });

    this.build(tint);
    this.group.position.set(this.arenaCx, 0, this.arenaCz);
    scene.add(this.group);
    events.emit("boss:spawned", undefined);
  }

  // ---- construção do humanoide ----
  private build(tint: number): void {
    const s = 1.7; // escala geral (boss grande)
    // sombra de contato
    const sh = new THREE.Mesh(new THREE.CircleGeometry(this.radius * 1.1, 24), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.4 }));
    sh.rotation.x = -Math.PI / 2; sh.position.y = 0.02; this.group.add(sh);

    // pernas
    for (const sx of [-1, 1]) {
      const leg = new THREE.Group();
      const thigh = new THREE.Mesh(new THREE.CylinderGeometry(0.16 * s, 0.13 * s, 0.7 * s, 8), this.bodyMat);
      thigh.position.y = 0.75 * s; thigh.castShadow = true; leg.add(thigh);
      const shin = new THREE.Mesh(new THREE.CylinderGeometry(0.12 * s, 0.1 * s, 0.7 * s, 8), this.bodyMat);
      shin.position.y = 0.3 * s; shin.castShadow = true; leg.add(shin);
      leg.position.set(sx * 0.22 * s, 0, 0);
      this.group.add(leg); this.legs.push(leg);
    }
    // torso (armadura)
    const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.3 * s, 0.42 * s, 1.0 * s, 10), this.bodyMat);
    torso.position.y = 1.55 * s; torso.castShadow = true; this.group.add(torso);
    const chest = new THREE.Mesh(new THREE.IcosahedronGeometry(0.14 * s, 0), this.glowMat); // núcleo brilhante no peito
    chest.position.set(0, 1.6 * s, 0.36 * s); this.group.add(chest);
    // ombreiras
    for (const sx of [-1, 1]) {
      const pad = new THREE.Mesh(new THREE.SphereGeometry(0.2 * s, 8, 6), this.bodyMat);
      pad.scale.set(1, 0.7, 1); pad.position.set(sx * 0.42 * s, 2.02 * s, 0); pad.castShadow = true; this.group.add(pad);
    }
    // cabeça + coroa
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.24 * s, 12, 10), this.bodyMat);
    head.position.y = 2.28 * s; head.castShadow = true; this.group.add(head);
    const eyeMat = new THREE.MeshBasicMaterial({ color: tint });
    for (const sx of [-1, 1]) { const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05 * s, 6, 6), eyeMat); eye.position.set(sx * 0.09 * s, 2.3 * s, 0.2 * s); this.group.add(eye); }
    const crown = new THREE.Group(); crown.position.y = 2.52 * s;
    for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; const sp = new THREE.Mesh(new THREE.ConeGeometry(0.05 * s, 0.28 * s, 4), this.glowMat); sp.position.set(Math.cos(a) * 0.2 * s, 0, Math.sin(a) * 0.2 * s); crown.add(sp); }
    this.group.add(crown);
    // braço esquerdo (livre)
    const armL = new THREE.Mesh(new THREE.CylinderGeometry(0.09 * s, 0.08 * s, 0.9 * s, 7), this.bodyMat);
    armL.position.set(-0.5 * s, 1.6 * s, 0); armL.rotation.z = 0.3; armL.castShadow = true; this.group.add(armL);
    // braço direito + CETRO (grupo que balança no ataque)
    this.weaponArm = new THREE.Group();
    this.weaponArm.position.set(0.5 * s, 2.0 * s, 0);
    const armR = new THREE.Mesh(new THREE.CylinderGeometry(0.1 * s, 0.09 * s, 0.95 * s, 7), this.bodyMat);
    armR.position.set(0, -0.45 * s, 0); armR.castShadow = true; this.weaponArm.add(armR);
    const staff = new THREE.Mesh(new THREE.CylinderGeometry(0.06 * s, 0.06 * s, 2.2 * s, 8), this.bodyMat);
    staff.position.set(0, -0.7 * s, 0.1 * s); this.weaponArm.add(staff);
    const orb = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22 * s, 0), this.glowMat);
    orb.position.set(0, 0.45 * s, 0.1 * s); this.weaponArm.add(orb);
    this.group.add(this.weaponArm);
    // capa
    this.cape = new THREE.Mesh(new THREE.PlaneGeometry(0.9 * s, 1.6 * s, 1, 3), this.capeMat);
    this.cape.position.set(0, 1.5 * s, -0.35 * s); this.group.add(this.cape);
  }

  // ---- Damageable / HookTarget ----
  get position(): THREE.Vector3 { return this.group.position; }
  get isDead(): boolean { return this.state === "dead"; }
  get invulnerable(): boolean { return this.state === "intro" || this.state === "dead"; }
  get armor(): number { return cfg.armor; }
  onHit(): void { if (!this.isDead) this.flash = 0.12; }
  applyKnockback(): void { /* rei é pesado: ignora */ }
  onHooked(): HookReaction { if (this.state === "chase") { this.reactStagger = 0.5; return "grab"; } return "none"; }

  // ---- Boss (HUD/debug) ----
  get hpFraction(): number { return Math.max(0, this.health.fraction); }
  get phaseNumber(): number { return this.phase; }
  get nameKey(): string { return this.titleKey ?? "boss.voidKing"; }
  get arenaInfo(): { x: number; z: number; r: number } { return { x: this.arenaCx, z: this.arenaCz, r: this.arenaR }; }
  private get speedMult(): number { return this.phase >= 2 ? cfg.phase2.speedMult : 1; }
  private get atkDmgMult(): number { return this.dmgMult * (this.phase >= 2 ? cfg.phase2.dmgMult : 1); }
  debugSnapshot(): BossDebug { return { state: this.state, attack: this.attackName ?? "-", phase: this.phase, hp: Math.ceil(this.health.current) }; }

  // ---- loop ----
  update(dt: number): void {
    if (this.state === "dead") { this.updateDeath(dt); return; }
    (Object.keys(this.cd) as KAttack[]).forEach((k) => (this.cd[k] = Math.max(0, this.cd[k] - dt)));
    this.restTimer = Math.max(0, this.restTimer - dt);
    this.reactStagger = Math.max(0, this.reactStagger - dt);
    this.resolvePending(dt);
    if (this.flash > 0) { this.flash -= dt; this.bodyMat.emissiveIntensity = this.flash > 0 ? 1.8 : this.phase >= 2 ? 0.9 : 0.5; }
    this.auraPulse += dt * (this.phase >= 2 ? 6 : 3);
    this.glowMat.emissiveIntensity = 1.2 + Math.sin(this.auraPulse) * 0.35 + (this.phase >= 2 ? 0.4 : 0);
    if (this.cape) this.cape.rotation.x = Math.sin(this.auraPulse * 0.6) * 0.12 - 0.1;

    // fúria (fase 2) ao cruzar 50%
    if (this.phase === 1 && this.health.fraction <= cfg.phase2.hpBelow) { this.phase = 2; this.deps.impulse(0.2); this.deps.telegraph.circle(this.group.position.x, this.group.position.z, this.arenaR, 0.6, 0xff4d6d); events.emit("boss:phase", { phase: 2 }); }

    this.faceAndUpdate(dt);

    if (this.state === "intro") { this.stateTimer -= dt; if (this.stateTimer <= 0) this.state = "chase"; return; }
    if (this.state === "chase") this.updateChase(dt);
    else if (this.state === "attack") this.updateAttack(dt);

    this.clampToArena();
  }

  private faceAndUpdate(dt: number): void {
    const p = this.deps.player.position;
    const want = Math.atan2(p.x - this.group.position.x, p.z - this.group.position.z);
    let d = want - this.facing; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    this.facing += d * Math.min(1, dt * 6);
    this.group.rotation.y = this.facing;
  }

  private distToPlayer(): number { _v.subVectors(this.deps.player.position, this.group.position); _v.y = 0; return _v.length(); }

  private updateChase(dt: number): void {
    if (this.reactStagger > 0) return;
    const dist = this.distToPlayer();
    if (this.restTimer <= 0) { const name = this.pickAttack(dist); if (name) { this.begin(name); return; } }
    // caminha até o alcance do golpe
    if (dist > cfg.slam.range * 0.85) {
      _v.subVectors(this.deps.player.position, this.group.position); _v.y = 0;
      const d = _v.length() || 1;
      const closing = dist > 8 ? cfg.moveSpeed * 0.7 : 0;
      this.group.position.addScaledVector(_v, ((cfg.moveSpeed * this.speedMult + closing) * dt) / d);
      this.gait += dt * 8; const sw = Math.sin(this.gait) * 0.35;
      if (this.legs[0]) this.legs[0].rotation.x = sw;
      if (this.legs[1]) this.legs[1].rotation.x = -sw;
    } else { if (this.legs[0]) this.legs[0].rotation.x *= 0.8; if (this.legs[1]) this.legs[1].rotation.x *= 0.8; }
  }

  private pickAttack(dist: number): KAttack | null {
    if (this.cd.summon <= 0) return "summon"; // convoca sempre que puder (tema comandante)
    if (this.phase >= 2 && this.cd.shockwave <= 0) return "shockwave";
    if (dist <= cfg.slam.range && this.cd.slam <= 0) return "slam";
    return null;
  }

  private begin(name: KAttack): void {
    this.state = "attack"; this.attackName = name; this.atkPhase = 0;
    if (name === "slam") this.atkTimer = cfg.slam.windup;
    else if (name === "summon") this.atkTimer = cfg.summon.windup;
    else this.atkTimer = cfg.shockwave.windup;
  }

  private updateAttack(dt: number): void {
    this.atkTimer -= dt;
    const n = this.attackName;
    if (n === "slam") {
      // ergue o cetro no windup
      this.weaponArm.rotation.x = THREE.MathUtils.lerp(this.weaponArm.rotation.x, -1.3, dt * 8);
      if (this.atkPhase === 0) { this.deps.telegraph.circle(this.group.position.x + Math.sin(this.facing) * 1.5, this.group.position.z + Math.cos(this.facing) * 1.5, cfg.slam.radius, 0.4, 0xff4d6d); }
      if (this.atkTimer <= 0 && this.atkPhase === 0) {
        this.atkPhase = 1; this.weaponArm.rotation.x = 0.9; // baixa o cetro
        const cx = this.group.position.x + Math.sin(this.facing) * 1.5, cz = this.group.position.z + Math.cos(this.facing) * 1.5;
        this.pendingSlam.push({ x: cx, z: cz, r: cfg.slam.radius, t: 0.05, dmg: cfg.slam.damage * this.atkDmgMult, kb: cfg.slam.knockback });
        this.deps.impulse(0.18); this.deps.particles.burst(cx, 0.6, cz, 0xff6b8a, { count: 24, speed: 10, dur: 0.6 });
        this.atkTimer = cfg.slam.recovery;
      } else if (this.atkTimer <= 0) { this.endAttack("slam", cfg.slam.cooldown); }
    } else if (n === "summon") {
      this.weaponArm.rotation.x = THREE.MathUtils.lerp(this.weaponArm.rotation.x, -1.6, dt * 6);
      if (this.atkTimer <= 0) {
        const count = this.phase >= 2 ? cfg.summon.countFury : cfg.summon.count;
        _c.copy(this.group.position);
        this.deps.summon(count, _c);
        this.deps.particles.burst(this.group.position.x, 1.5, this.group.position.z, 0x9b6bff, { count: 30, speed: 8, dur: 0.8 });
        this.deps.telegraph.circle(this.group.position.x, this.group.position.z, 3.5, 0.5, 0x9b6bff);
        this.endAttack("summon", cfg.summon.cooldown);
      }
    } else if (n === "shockwave") {
      if (this.atkPhase === 0) { this.deps.telegraph.circle(this.group.position.x, this.group.position.z, cfg.shockwave.radius, 0.5, 0xff4d6d); }
      if (this.atkTimer <= 0) {
        this.pendingWave.push({ x: this.group.position.x, z: this.group.position.z, r: cfg.shockwave.radius, t: 0.05, dmg: cfg.shockwave.damage * this.atkDmgMult, kb: cfg.shockwave.knockback });
        this.deps.impulse(0.24); this.deps.particles.burst(this.group.position.x, 0.7, this.group.position.z, 0xff6b8a, { count: 40, speed: 16, dur: 0.7 });
        this.endAttack("shockwave", cfg.shockwave.cooldown);
      }
    }
  }

  private endAttack(name: KAttack, cd: number): void {
    this.cd[name] = cd; this.attackName = null; this.state = "chase";
    this.restTimer = cfg.restBetween; this.weaponArm.rotation.x = 0;
  }

  private resolvePending(dt: number): void {
    for (let i = this.pendingSlam.length - 1; i >= 0; i--) { const s = this.pendingSlam[i]; s.t -= dt; if (s.t <= 0) { this.hitPlayerInCircle(s.x, s.z, s.r, s.dmg, s.kb); this.pendingSlam.splice(i, 1); } }
    for (let i = this.pendingWave.length - 1; i >= 0; i--) { const w = this.pendingWave[i]; w.t -= dt; if (w.t <= 0) { this.hitPlayerInCircle(w.x, w.z, w.r, w.dmg, w.kb); this.pendingWave.splice(i, 1); } }
  }

  private hitPlayerInCircle(cx: number, cz: number, r: number, dmg: number, kb: number): void {
    const p = this.deps.player;
    const dx = p.position.x - cx, dz = p.position.z - cz;
    if (dx * dx + dz * dz <= r * r) {
      _c.set(cx, 0, cz);
      dealDamage(p, { baseDamage: dmg, critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: _c, knockbackForce: kb });
    }
  }

  private clampToArena(): void {
    const lim = this.arenaR + GameConfig.boss.leash.margin - this.radius;
    const dx = this.group.position.x - this.arenaCx, dz = this.group.position.z - this.arenaCz;
    const d = Math.hypot(dx, dz);
    if (d > lim && d > 1e-4) { this.group.position.x = this.arenaCx + (dx / d) * lim; this.group.position.z = this.arenaCz + (dz / d) * lim; }
  }

  private die(): void {
    if (this.state === "dead") return;
    this.state = "dead"; this.attackName = null; this.deathTimer = 1.6; this.deps.impulse(0.24);
    events.emit(this.isEvent ? "boss:defeatedEvent" : "boss:defeated", undefined);
  }
  private updateDeath(dt: number): void {
    this.deathTimer -= dt; const t = Math.max(0, this.deathTimer / 1.6);
    this.group.scale.setScalar(t); this.group.position.y += dt * 0.8; this.glowMat.emissiveIntensity = 3 * t;
    if (Math.random() < dt * 10) this.deps.particles.burst(this.group.position.x + (Math.random() - 0.5) * 3, Math.random() * 3, this.group.position.z + (Math.random() - 0.5) * 3, 0x9b6bff, { count: 6, speed: 4 });
    if (this.deathTimer <= 0) this.removeRequested = true;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    this.group.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    this.bodyMat.dispose(); this.capeMat.dispose(); this.glowMat.dispose();
  }
}
